import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import './ConsultantPage.css';

const INITIAL_FORM = {
  fullName: '',
  email: '',
  phone: '',
  query: '',
  message: '',
};

function ConsultantPage() {
  const { slug } = useParams();

  const [consultant, setConsultant] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | notfound | error
  const [form, setForm] = useState(INITIAL_FORM);
  const [formState, setFormState] = useState({ status: 'idle', msg: '' });

  // Fetch the consultant by slug.
  useEffect(() => {
    let active = true;
    setStatus('loading');
    api.getConsultant(slug)
      .then((data) => {
        if (!active) return;
        if (data && data.slug) {
          setConsultant(data);
          setStatus('ready');
        } else {
          setStatus('notfound');
        }
      })
      .catch((err) => {
        if (!active) return;
        setStatus(/404|not found/i.test(err.message || '') ? 'notfound' : 'error');
      });
    return () => { active = false; };
  }, [slug]);

  // SEO: title + description + OG/Twitter tags when the consultant resolves.
  // Hooks must be unconditional — guard inside the effect body.
  useEffect(() => {
    if (!consultant) return;

    const baseTitle = consultant.meta_title || consultant.name || 'Consultant';
    document.title = `${baseTitle} · Mind Spa`;

    const desc = (consultant.meta_description || consultant.summary || consultant.tagline || '').slice(0, 240);
    const image = consultant.image || '';
    const url = `${window.location.origin}/cons/${consultant.slug}`;

    const setMeta = (key, value, kind = 'name') => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${kind}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(kind, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    const setCanonical = (href) => {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    setMeta('description', desc);
    setCanonical(url);
    setMeta('og:type', 'profile', 'property');
    setMeta('og:url', url, 'property');
    setMeta('og:title', baseTitle, 'property');
    setMeta('og:description', desc, 'property');
    if (image) {
      setMeta('og:image', image, 'property');
      setMeta('og:image:secure_url', image, 'property');
      setMeta('og:image:alt', baseTitle, 'property');
    }
    setMeta('twitter:title', baseTitle);
    setMeta('twitter:description', desc);
    if (image) setMeta('twitter:image', image);

    return () => { document.title = 'Mind Spa'; };
  }, [consultant]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consultant) return;
    setFormState({ status: 'sending', msg: '' });
    try {
      const fullName = (form.fullName || '').trim();
      const lines = [];
      if (form.query) lines.push(`Concern: ${form.query}`);
      if (form.message) lines.push('', form.message);
      const message = lines.join('\n') || form.message || `Consultation request for ${consultant.name}.`;

      const res = await api.submitEnquiry({
        name: fullName,
        email: form.email,
        phone: form.phone,
        topic: `Consultation request — ${consultant.name}`,
        message,
        consultant_id: consultant.id,
      });
      setFormState({ status: 'ok', msg: res?.message || "Thanks — we'll be in touch shortly." });
      setForm(INITIAL_FORM);
    } catch (err) {
      setFormState({
        status: 'error',
        msg: err.message || "Couldn't send your request. Please try WhatsApp instead.",
      });
    }
  };

  if (status === 'loading') {
    return (
      <div className="cons-page">
        <div className="container cons-state">
          <div className="cons-spinner" />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (status === 'notfound' || status === 'error') {
    return (
      <div className="cons-page">
        <div className="container cons-state">
          <h1>Consultant not found</h1>
          <p>This consultant page doesn’t exist or is no longer available.</p>
          <Link to="/contact" className="btn-primary">Contact Mind Spa</Link>
        </div>
      </div>
    );
  }

  const c = consultant;
  const tags = Array.isArray(c.tags) ? c.tags : [];
  const sections = Array.isArray(c.sections) ? c.sections : [];
  const testimonials = Array.isArray(c.testimonials) ? c.testimonials : [];

  const initialOf = (name) => {
    const w = (name || '').trim().replace(/^[-–—]\s*/, '');
    return w ? w[0].toUpperCase() : '“';
  };

  return (
    <main className="cons-page">
      {/* Hero */}
      <section className="cons-hero">
        <div className="container cons-hero__inner">
          <div className="cons-hero__avatar" style={c.gradient ? { background: c.gradient } : undefined}>
            {c.image
              ? <img src={c.image} alt={c.name} loading="eager" decoding="async" />
              : <span className="cons-hero__initials">{c.initials || c.name?.[0] || '?'}</span>}
          </div>
          <div className="cons-hero__intro">
            <nav className="cons-breadcrumb">
              <Link to="/">Home</Link><span>/</span><span>Consultation</span>
            </nav>
            <h1 className="cons-hero__name">{c.name}</h1>
            {c.title && <p className="cons-hero__title">{c.title}</p>}
            {c.tagline && <p className="cons-hero__tagline">{c.tagline}</p>}
            {tags.length > 0 && (
              <div className="cons-chips">
                {tags.map((t) => <span key={t} className="cons-chip">{t}</span>)}
              </div>
            )}
            <a href="#consult" className="btn-primary cons-hero__cta">Request a Consultation</a>
          </div>
        </div>
      </section>

      <section className="cons-body">
        <div className="container cons-layout">
          {/* Left: profile */}
          <div className="cons-main">
            {c.summary && (
              <div className="cons-summary">
                {c.summary.split(/\n\n+/).filter(Boolean).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}

            {sections.length > 0 && (
              <div className="cons-sections">
                <h2 className="cons-sections__title">Specializations &amp; Target Groups</h2>
                <div className="cons-sections__grid">
                  {sections.map((s, i) => (
                    <div key={i} className="cons-section-card">
                      <h3>{s.heading}</h3>
                      <p>{s.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: consultation enquiry form */}
          <aside className="cons-aside" id="consult">
            <div className="cons-form-card">
              <h2 className="cons-form-card__title">Take Up a Consultation</h2>
              <p className="cons-form-card__sub">
                Send your details and {c.name?.split(' ')[0] || 'we'} will get back to you to arrange a session.
              </p>

              {formState.status === 'ok' && (
                <div className="cons-alert cons-alert--ok">{formState.msg}</div>
              )}
              {formState.status === 'error' && (
                <div className="cons-alert cons-alert--err">{formState.msg}</div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="cons-field">
                  <label>Full Name *</label>
                  <input name="fullName" type="text" value={form.fullName} onChange={handleChange}
                         placeholder="Your full name" required />
                </div>
                <div className="cons-field">
                  <label>Email *</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange}
                         placeholder="your@email.com" required />
                </div>
                <div className="cons-field">
                  <label>Phone / WhatsApp *</label>
                  <input name="phone" type="tel" value={form.phone} onChange={handleChange}
                         placeholder="+91 XXXXX XXXXX" required />
                </div>
                <div className="cons-field">
                  <label>What would you like help with? *</label>
                  <input name="query" type="text" value={form.query} onChange={handleChange}
                         placeholder="e.g. stress, burnout, anxiety" required />
                </div>
                <div className="cons-field">
                  <label>Message</label>
                  <textarea name="message" value={form.message} onChange={handleChange}
                            placeholder="Anything you'd like to share (optional)" />
                </div>
                <button type="submit" className="btn-primary cons-form-card__btn"
                        disabled={formState.status === 'sending'}>
                  {formState.status === 'sending' ? 'Sending…' : 'Send Request'}
                </button>
              </form>

              {c.phone && (
                <p className="cons-form-card__phone">
                  Prefer to call? <a href={`tel:${c.phone}`}>{c.phone}</a>
                </p>
              )}
            </div>
          </aside>
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="cons-testi">
          <div className="container">
            <p className="cons-testi__label">In their words</p>
            <h2 className="cons-testi__title">What clients say</h2>
            <div className="cons-testi__grid">
              {testimonials.map((t, i) => (
                <figure key={i} className="cons-testi-card">
                  <blockquote className="cons-testi-card__quote">{t.quote}</blockquote>
                  {t.author && (
                    <figcaption className="cons-testi-card__author">
                      <span className="cons-testi-card__avatar" aria-hidden="true">{initialOf(t.author)}</span>
                      <span className="cons-testi-card__meta">
                        <span className="cons-testi-card__name">{t.author.replace(/^[-–—]\s*/, '')}</span>
                        <span className="cons-testi-card__role">Verified client</span>
                      </span>
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

export default ConsultantPage;
